import { useState, useRef } from 'react';
import { SCREEN_TIME_OPTIONS } from '../../lib/detoxPrompts';
import DetoxShareCard from './DetoxShareCard';
import { downloadCardAsImage } from '../../lib/shareCard';

const MOOD_OPTIONS = [
  { value: 1, emoji: '😔', label: 'Pesado' },
  { value: 2, emoji: '😐', label: 'Neutro' },
  { value: 3, emoji: '🙂', label: 'Leve' },
  { value: 4, emoji: '😊', label: 'Bem' },
  { value: 5, emoji: '🤩', label: 'Transformado' },
];

export default function DetoxCompletion({ enrollment, checkins, onComplete, alreadyCompleted }) {
  const [finalScreenTime, setFinalScreenTime] = useState(enrollment?.final_screen_time || '');
  const [finalMood, setFinalMood]             = useState(enrollment?.final_mood || null);
  const [loading, setLoading]                 = useState(false);
  const [downloading, setDownloading]         = useState(false);
  const shareCardRef                          = useRef(null);

  const handleComplete = async () => {
    if (!finalScreenTime || !finalMood) return;
    setLoading(true);
    await onComplete({ finalScreenTime, finalMood });
    setLoading(false);
  };

  const handleDownloadCard = async () => {
    if (!shareCardRef.current) return;
    setDownloading(true);
    await downloadCardAsImage(shareCardRef.current, 'clube-do-eu-detox.png');
    setDownloading(false);
  };

  // Stats
  const daysStayedOff    = checkins.filter(c => c.stayed_off).length;
  const avgCraving       = checkins.length
    ? Math.round(checkins.reduce((s, c) => s + (c.craving_score || 0), 0) / checkins.length)
    : 0;
  const initial          = enrollment?.initial_screen_time || '';
  const final            = alreadyCompleted ? enrollment?.final_screen_time : finalScreenTime;

  // Hidden share card — always rendered so ref is available
  const completedEnrollment = alreadyCompleted
    ? enrollment
    : { ...enrollment, final_screen_time: finalScreenTime };

  return (
    <div className="detox-completion">
      {/* Off-screen card for html2canvas capture */}
      <DetoxShareCard
        ref={shareCardRef}
        enrollment={completedEnrollment}
        checkins={checkins}
      />
      <div className="detox-hero">
        <div className="detox-hero-emoji">🧘</div>
        <h1 className="detox-hero-title">Você chegou lá!</h1>
        <p className="detox-hero-subtitle">7 dias de presença. Isso é real.</p>
      </div>

      {/* Before / After */}
      <div className="card before-after-card">
        <p className="card-title">Antes e depois</p>
        <div className="before-after-row">
          <div className="before-after-col">
            <p className="before-after-label">📱 Antes do detox</p>
            <p className="before-after-value">{initial}</p>
            <p className="before-after-sub">nas redes por dia</p>
          </div>
          <div className="before-after-arrow">→</div>
          <div className="before-after-col highlight">
            <p className="before-after-label">✨ Agora</p>
            <p className="before-after-value">{final || '?'}</p>
            <p className="before-after-sub">nas redes por dia</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="profile-stats" style={{ marginBottom: 16 }}>
        <div className="stat-card">
          <div className="stat-value">{daysStayedOff}</div>
          <div className="stat-label">Dias sem redes 📵</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{checkins.length}</div>
          <div className="stat-label">Reflexões escritas ✍️</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{avgCraving}x</div>
          <div className="stat-label">Craving médio por dia</div>
        </div>
      </div>

      {!alreadyCompleted ? (
        <>
          {/* Final screen time */}
          <div className="card">
            <p className="card-title">📱 E agora, quanto tempo você passa nas redes?</p>
            <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginBottom: 12 }}>
              Você começou com <strong>{initial}</strong>. Seja honesta!
            </p>
            <div className="screen-time-options">
              {SCREEN_TIME_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`screen-time-btn ${finalScreenTime === opt.value ? 'selected' : ''}`}
                  onClick={() => setFinalScreenTime(opt.value)}
                  type="button"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Final mood */}
          <div className="card">
            <p className="card-title">Como você está se sentindo?</p>
            <div className="mood-options">
              {MOOD_OPTIONS.map(m => (
                <button
                  key={m.value}
                  className={`mood-option ${finalMood === m.value ? 'selected' : ''}`}
                  data-mood={m.value}
                  onClick={() => setFinalMood(m.value)}
                  type="button"
                >
                  <span className="mood-emoji">{m.emoji}</span>
                  <span className="mood-text">{m.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            className="btn btn-primary detox-start-btn"
            onClick={handleComplete}
            disabled={!finalScreenTime || !finalMood || loading}
          >
            {loading ? 'Finalizando...' : 'Concluir o Desafio 🧘'}
          </button>
        </>
      ) : (
        /* Already completed — show CTA */
        <div className="detox-cta-card">
          <div className="detox-badge-earned">
            <span className="detox-badge-icon">🧘</span>
            <div>
              <p className="detox-badge-name">Badge <strong>Mente Livre</strong> desbloqueado!</p>
              <p className="detox-badge-desc">Aparece no seu perfil com destaque especial.</p>
            </div>
          </div>

          {/* Share button */}
          <div className="share-btn-wrap">
            <button
              className="btn btn-primary detox-start-btn"
              onClick={handleDownloadCard}
              disabled={downloading}
            >
              {downloading ? 'Gerando imagem...' : '📲 Baixar card para o Instagram'}
            </button>
            <p className="share-btn-hint">
              Salva a imagem e compartilha nos seus Stories 🧘
            </p>
          </div>

          <div className="detox-offer">
            <p className="detox-offer-label">✨ Próximo passo</p>
            <h3 className="detox-offer-title">Programa "30 Dias de Presença"</h3>
            <p className="detox-offer-desc">
              Você provou que consegue 7 dias. Agora vá mais fundo — 30 prompts guiados
              sobre atenção, identidade e presença. Um por dia, no seu ritmo.
            </p>
            <p className="detox-offer-price">Em breve 🚀</p>
          </div>
        </div>
      )}
    </div>
  );
}
